/**
 * Toast — Lightweight notification system
 * Uso: const { toast } = useToast(); toast.success("Guardado");
 */
import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X, Sparkles, Loader2 } from "../lib/icons.jsx";
import { cn } from "../lib/utils.js";

const ToastContext = createContext(null);

let nextId = 1;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const remove = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const add = useCallback((variant, message, opts = {}) => {
    const id = nextId++;
    const toast = {
      id,
      variant,
      message,
      description: opts.description,
      action: opts.action,            // { label, onClick }
      duration: opts.duration ?? (variant === "loading" ? null : (opts.action ? 8000 : 4000)),
    };
    setToasts(prev => [...prev, toast]);
    if (toast.duration !== null) {
      setTimeout(() => remove(id), toast.duration);
    }
    return id;
  }, [remove]);

  const update = useCallback((id, variant, message, opts = {}) => {
    setToasts(prev => prev.map(t =>
      t.id === id ? { ...t, variant, message, description: opts.description } : t
    ));
    const duration = opts.duration ?? 4000;
    if (duration !== null) {
      setTimeout(() => remove(id), duration);
    }
  }, [remove]);

  const toast = {
    success: (msg, opts) => add("success", msg, opts),
    error:   (msg, opts) => add("error",   msg, opts),
    warning: (msg, opts) => add("warning", msg, opts),
    info:    (msg, opts) => add("info",    msg, opts),
    ai:      (msg, opts) => add("ai",      msg, opts),
    loading: (msg, opts) => add("loading", msg, opts),
    update,
    dismiss: remove,
  };

  // v7.9.1 · Shim global · reemplaza window.alert por toast.error para eliminar
  // el diálogo bloqueante del navegador. 20 callsites con alert() migrados de una.
  //   Preserva el alert original en window.__nativeAlert por si algo lo necesita.
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.__nativeAlert) window.__nativeAlert = window.alert;
    window.alert = (msg) => {
      const text = msg == null ? "" : String(msg);
      // Detectar si viene con prefix "Error" para elegir variant, sino usar error igual (alert nunca es "good news")
      add("error", text.slice(0, 200), { duration: 5000 });
      console.warn("[alert-shim]", text);
    };
    return () => {
      if (window.__nativeAlert) window.alert = window.__nativeAlert;
    };
  }, [add]);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <ToastViewport toasts={toasts} onClose={remove} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}

/* ── Viewport ───────────────────────────────────────────────────────────── */
function ToastViewport({ toasts, onClose }) {
  return (
    <div className="fixed top-[max(1rem,env(safe-area-inset-top))] left-4 right-4 sm:left-auto z-[100] flex flex-col gap-2 sm:max-w-sm sm:w-full pointer-events-none">
      {toasts.map(t => (
        <ToastItem key={t.id} toast={t} onClose={() => onClose(t.id)} />
      ))}
    </div>
  );
}

const VARIANT_STYLES = {
  success: {
    icon: CheckCircle2,
    classes: "bg-[#ECFDF5] border-[#A7F3D0] text-[#065F46]",
    iconClass: "text-[#059669]",
  },
  error: {
    icon: AlertCircle,
    classes: "bg-[#FEF2F2] border-[#FECACA] text-[#B91C1C]",
    iconClass: "text-[#DC2626]",
  },
  warning: {
    icon: AlertTriangle,
    classes: "bg-[#FFFBEB] border-[#FDE68A] text-[#92400E]",
    iconClass: "text-[#D97706]",
  },
  info: {
    icon: Info,
    classes: "bg-[#E0F7FB] border-[#A5F3FC] text-[#155E75]",
    iconClass: "text-[#0E7490]",
  },
  ai: {
    icon: Sparkles,
    classes: "bg-[#F5F3FF] border-[#DDD6FE] text-[#DDD6FE]",
    iconClass: "text-[#7C3AED]",
  },
  loading: {
    icon: Loader2,
    classes: "bg-card/95 border-border text-foreground",
    iconClass: "text-accent animate-spin",
  },
};

function ToastItem({ toast: t, onClose }) {
  const variant = VARIANT_STYLES[t.variant] ?? VARIANT_STYLES.info;
  const Icon    = variant.icon;
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setExiting(false), 10);
    return () => clearTimeout(id);
  }, []);

  function handleClose() {
    setExiting(true);
    setTimeout(onClose, 150);
  }

  return (
    <div
      className={cn(
        "pointer-events-auto flex items-start gap-3 px-4 py-3 rounded-xl border shadow-lg shadow-black/30 backdrop-blur-md",
        "transition-all duration-200 ease-out",
        exiting ? "opacity-0 translate-x-4" : "opacity-100 translate-x-0",
        variant.classes
      )}
      style={{ animation: "slideInRight 0.2s ease-out" }}
    >
      <Icon className={cn("h-4 w-4 flex-shrink-0 mt-0.5", variant.iconClass)} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium leading-tight">{t.message}</p>
        {t.description && (
          <p className="text-xs opacity-70 mt-1">{t.description}</p>
        )}
        {t.action && (
          <button
            onClick={() => { t.action.onClick?.(); handleClose(); }}
            className="mt-1.5 text-xs font-semibold underline hover:no-underline opacity-90 hover:opacity-100"
          >
            {t.action.label}
          </button>
        )}
      </div>
      <button
        onClick={handleClose}
        aria-label="Cerrar notificación"
        className="text-current opacity-50 hover:opacity-100 transition-opacity flex-shrink-0"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
